import { z } from 'zod';

/**
 * Zod schema for validating the application configuration
 * This schema validates both the default config and environment-specific overrides
 */
export const configSchema = z.object({
  // App Configuration
  nodeEnv: z.enum(['development', 'production', 'test']),
  appVersion: z.string(),
  appEnv: z.enum(['local', 'predev', 'devcb', 'production']),

  // Protocol Configuration
  cbProtocol: z.enum(['http', 'https']),
  disableApp: z.boolean(),

  // Domain and API Configuration
  cbDomain: z.string(),
  cbAiApiUrl: z.string().url(),
  beehiveUrl: z.string().url(),
  enableEditCodeSamples: z.boolean(),
  apiDocsBaseUrl: z.string().url(),

  // OpenAPI & Code Samples Configuration
  openApiSpecUrl: z.string(),
  samplesDirectoryPath: z.string(),
  codeSamplesBaseDirName: z.string(),
  codeSamplesFileName: z.string(),
  codeSamplesRelativePath: z.string(),
  tocRelativePath: z.string(),

  // Next.js Configuration
  basePath: z.string(),

  // Analytics & Tracking (optional)
  gtmId: z.string().nullable(),
  ga4MeasurementId: z.string().nullable(),
  /** Injected at build via NEXT_PUBLIC_PENDO_API_KEY only — not read from config.yml */
  pendoApiKey: z.string().nullable().default(null),

  // reCAPTCHA Configuration
  recaptchaEnabled: z.boolean(),
  recaptchaPublicKey: z.string().nullable(),

  // Sentry Configuration
  sentryEnabled: z.boolean(),
  sentryDsn: z.string().url().nullable(),
});

/**
 * Schema for the raw YAML configuration file structure
 * This validates the structure before merging defaults with environment overrides
 */
export const rawConfigSchema = z.object({
  default: z.object({
    // App Configuration
    nodeEnv: z.enum(['development', 'production', 'test']),
    appVersion: z.string(),

    // Protocol Configuration
    cbProtocol: z.enum(['http', 'https']),
    disableApp: z.boolean(),

    // OpenAPI & Code Samples Configuration
    openApiSpecUrl: z.string(),
    samplesDirectoryPath: z.string(),
    codeSamplesBaseDirName: z.string(),
    codeSamplesFileName: z.string(),
    codeSamplesRelativePath: z.string(),
    tocRelativePath: z.string(),

    // Next.js Configuration
    basePath: z.string(),

    // Analytics & Tracking (optional)
    gtmId: z.string().nullable(),
    ga4MeasurementId: z.string().nullable(),

    // reCAPTCHA Configuration
    recaptchaEnabled: z.boolean(),
    recaptchaPublicKey: z.string().nullable(),

    // Sentry Configuration
    sentryEnabled: z.boolean(),
    sentryDsn: z.string().url().nullable(),
  }),

  environments: z.object({
    local: z.object({
      cbDomain: z.string(),
      cbAiApiUrl: z.string().url(),
      beehiveUrl: z.string().url(),
      enableEditCodeSamples: z.boolean(),
      apiDocsBaseUrl: z.string().url(),
      recaptchaEnabled: z.boolean(),
      recaptchaPublicKey: z.string().nullable(),
      sentryEnabled: z.boolean(),
      sentryDsn: z.string().url().nullable(),
    }),
    predev: z.object({
      cbDomain: z.string(),
      cbAiApiUrl: z.string().url(),
      beehiveUrl: z.string().url(),
      enableEditCodeSamples: z.boolean(),
      apiDocsBaseUrl: z.string().url(),
      recaptchaEnabled: z.boolean(),
      recaptchaPublicKey: z.string().nullable(),
      sentryEnabled: z.boolean(),
      sentryDsn: z.string().url().nullable(),
    }),
    devcb: z.object({
      cbDomain: z.string(),
      cbAiApiUrl: z.string().url(),
      beehiveUrl: z.string().url(),
      enableEditCodeSamples: z.boolean(),
      apiDocsBaseUrl: z.string().url(),
      recaptchaEnabled: z.boolean(),
      recaptchaPublicKey: z.string().nullable(),
      sentryEnabled: z.boolean(),
      sentryDsn: z.string().url().nullable(),
    }),
    production: z.object({
      cbDomain: z.string(),
      cbAiApiUrl: z.string().url(),
      beehiveUrl: z.string().url(),
      enableEditCodeSamples: z.boolean(),
      apiDocsBaseUrl: z.string().url(),
      recaptchaEnabled: z.boolean(),
      recaptchaPublicKey: z.string().nullable(),
      sentryEnabled: z.boolean(),
      sentryDsn: z.string().url().nullable(),
    }),
  }),
});

/**
 * TypeScript type for the final merged configuration
 */
export type Config = z.infer<typeof configSchema>;

/**
 * TypeScript type for the raw YAML configuration
 */
export type RawConfig = z.infer<typeof rawConfigSchema>;

/**
 * Valid environment names
 */
export type AppEnvironment = 'local' | 'predev' | 'devcb' | 'production';
